import { ChevronDown } from "reicon-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";

import {
	closeHubMenusForModalSelect,
	closeSiblingSelectMenus,
} from "../../lib/select-menu.js";
import {
	computeHubFieldSelectPopStyle,
	modalActionsTop,
	modalHeaderBottom,
	readViewportInsets,
} from "./hub-field-select-position.js";

export function HubFieldSelect({
	id,
	name,
	label,
	defaultValue,
	items,
	onPick,
	menuInModal,
}: {
	id: string;
	name: string;
	label: string;
	defaultValue: string;
	items: Array<[string, string]>;
	onPick?: (value: string) => void;
	/** When true, use a button trigger and portaled fixed menu (required inside scrollable modals). */
	menuInModal?: boolean;
}) {
	const rootRef = useRef<HTMLDivElement>(null);
	const detailsRef = useRef<HTMLDetailsElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const popRef = useRef<HTMLDivElement>(null);
	const [autoInModal, setAutoInModal] = useState(false);
	const inModal = menuInModal ?? autoInModal;
	const [open, setOpen] = useState(false);
	const [portalHost, setPortalHost] = useState<HTMLElement | null>(null);
	const [popStyle, setPopStyle] = useState<CSSProperties>({});
	const [value, setValue] = useState(defaultValue);

	useEffect(() => {
		setValue(defaultValue);
	}, [defaultValue]);

	useLayoutEffect(() => {
		if (menuInModal !== undefined) return;
		setAutoInModal(Boolean(rootRef.current?.closest("dialog.modal")));
	}, [menuInModal]);

	const current = items.find(([item]) => item === value)?.[1] ?? items[0]?.[1] ?? value;

	const closeMenu = useCallback(() => {
		if (inModal) {
			setOpen(false);
			setPortalHost(null);
			rootRef.current?.classList.remove("is-open");
			return;
		}
		const details = detailsRef.current;
		if (details?.open) details.open = false;
	}, [inModal]);

	const repositionPop = useCallback(() => {
		const anchor = inModal ? triggerRef.current : null;
		if (!anchor || !open) return;
		const measured = popRef.current?.scrollHeight ?? 0;
		const height = measured > 0 ? measured : Math.min(items.length * 36 + 12, 240);
		setPopStyle(
			computeHubFieldSelectPopStyle(
				anchor.getBoundingClientRect(),
				height,
				readViewportInsets(),
				modalActionsTop(anchor),
				modalHeaderBottom(anchor) + 4,
			),
		);
	}, [open, items.length, inModal]);

	useLayoutEffect(() => {
		if (!open || !inModal || !portalHost) return;
		repositionPop();
		const frame = window.requestAnimationFrame(repositionPop);
		const vv = window.visualViewport;
		const onViewportChange = () => repositionPop();
		window.addEventListener("resize", onViewportChange);
		vv?.addEventListener("resize", onViewportChange);
		vv?.addEventListener("scroll", onViewportChange);
		const scrollRoot = portalHost.querySelector(".modal-body");
		scrollRoot?.addEventListener("scroll", onViewportChange, { passive: true });
		document.addEventListener("scroll", onViewportChange, true);
		const resizeObserver =
			popRef.current && typeof ResizeObserver !== "undefined"
				? new ResizeObserver(onViewportChange)
				: null;
		if (popRef.current && resizeObserver) resizeObserver.observe(popRef.current);
		return () => {
			window.cancelAnimationFrame(frame);
			window.removeEventListener("resize", onViewportChange);
			vv?.removeEventListener("resize", onViewportChange);
			vv?.removeEventListener("scroll", onViewportChange);
			scrollRoot?.removeEventListener("scroll", onViewportChange);
			document.removeEventListener("scroll", onViewportChange, true);
			resizeObserver?.disconnect();
		};
	}, [open, inModal, portalHost, repositionPop]);

	useEffect(() => {
		const root = rootRef.current;
		if (!root) return;
		const onClose = () => closeMenu();
		root.addEventListener("bafe:hub-field-select-close", onClose);
		return () => root.removeEventListener("bafe:hub-field-select-close", onClose);
	}, [closeMenu]);

	useEffect(() => {
		if (!open) return;
		let active = true;
		let removeListeners: (() => void) | null = null;
		const timer = window.setTimeout(() => {
			if (!active) return;
			const onPointerDown = (event: PointerEvent) => {
				const target = event.target as Node | null;
				if (!target) return;
				if (rootRef.current?.contains(target)) return;
				if (popRef.current?.contains(target)) return;
				closeMenu();
			};
			const onKeyDown = (event: KeyboardEvent) => {
				if (event.key !== "Escape") return;
				closeMenu();
				(inModal ? triggerRef.current : detailsRef.current?.querySelector("summary"))?.focus();
			};
			document.addEventListener("pointerdown", onPointerDown);
			document.addEventListener("keydown", onKeyDown);
			removeListeners = () => {
				document.removeEventListener("pointerdown", onPointerDown);
				document.removeEventListener("keydown", onKeyDown);
			};
			if (!active) removeListeners();
		}, 0);
		return () => {
			active = false;
			window.clearTimeout(timer);
			removeListeners?.();
		};
	}, [open, closeMenu, inModal]);

	const pickItem = (item: string) => {
		setValue(item);
		onPick?.(item);
		closeMenu();
	};

	const openModalMenu = () => {
		const root = rootRef.current;
		if (!root) return;
		if (open) {
			closeMenu();
			return;
		}
		const dialog = root.closest("dialog.modal");
		const scrollRoot = dialog?.querySelector(".modal-body");
		const scrollTop = scrollRoot instanceof HTMLElement ? scrollRoot.scrollTop : null;
		closeHubMenusForModalSelect(root);
		setPortalHost(dialog instanceof HTMLElement ? dialog : null);
		root.classList.add("is-open");
		setOpen(true);
		if (scrollRoot instanceof HTMLElement && scrollTop !== null) {
			scrollRoot.scrollTop = scrollTop;
			window.requestAnimationFrame(() => {
				scrollRoot.scrollTop = scrollTop;
			});
		}
	};

	const optionButtons = items.map(([item, text]) => (
		<button
			key={item || "empty"}
			type="button"
			role="option"
			aria-selected={item === value}
			className={item === value ? "select-item active" : "select-item"}
			onClick={() => pickItem(item)}
		>
			{text}
		</button>
	));

	const modalListbox = (
		<div
			ref={popRef}
			className="select-pop hub-field-select-pop"
			role="listbox"
			aria-labelledby={`${id}-label`}
			style={open ? popStyle : undefined}
		>
			{optionButtons}
		</div>
	);

	if (inModal) {
		return (
			<div ref={rootRef} className={open ? "select hub-field-select is-open" : "select hub-field-select"}>
				<label id={`${id}-label`} htmlFor={id}>
					{label}
				</label>
				<input type="hidden" name={name} id={id} value={value} />
				<button
					ref={triggerRef}
					type="button"
					className="hub-field-select-trigger"
					aria-labelledby={`${id}-label`}
					aria-haspopup="listbox"
					aria-expanded={open}
					onClick={openModalMenu}
				>
					<span>{current}</span>
					<ChevronDown size={12} aria-hidden="true" />
				</button>
				{open && portalHost ? createPortal(modalListbox, portalHost) : null}
			</div>
		);
	}

	return (
		<div ref={rootRef}>
			<label id={`${id}-label`} htmlFor={id}>
				{label}
			</label>
			<input type="hidden" name={name} id={id} value={value} />
			<details
				ref={detailsRef}
				className="select hub-field-select"
				onToggle={(event) => {
					if (event.currentTarget.open) closeSiblingSelectMenus(event.currentTarget);
				}}
				onPointerDown={(event) => event.stopPropagation()}
			>
				<summary aria-labelledby={`${id}-label`}>
					<span>{current}</span>
					<ChevronDown size={12} aria-hidden="true" />
				</summary>
				<div className="select-pop" role="listbox" aria-labelledby={`${id}-label`}>
					{optionButtons}
				</div>
			</details>
		</div>
	);
}
